import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  model,
} from '@angular/core';
import { isArray, isEqual, isNumber, orderBy } from 'lodash';
import type { OnyxOption } from '../../../directives/interfaces';
import { OnyxDropdownDirective } from '../../../directives/onyx-dropdown.directive';
import { I18N_NAMESPACE } from '../../../internal/constants';
import { OnyxIconButtonComponent } from '../../buttons/onyx-icon-button/onyx-icon-button.component';
import { OnyxInputLabelComponent } from '../../labels/onyx-input-label/onyx-input-label.component';
import type { OnyxChip, OnyxChipsGroup } from '../interfaces';
import { OnyxChipComponent } from '../onyx-chip/onyx-chip.component';

@Component({
  selector: 'onyx-chips',
  imports: [
    OnyxChipComponent,
    OnyxDropdownDirective,
    OnyxIconButtonComponent,
    OnyxInputLabelComponent,
  ],
  templateUrl: './onyx-chips.component.html',
  styleUrl: './onyx-chips.component.scss',
})
export class OnyxChipsComponent<GroupType, ChipType> implements OnInit {
  private readonly injector = inject(Injector);

  public readonly chips = input.required({
    transform: (
      chips:
        | OnyxChipsGroup<GroupType, ChipType>[]
        | OnyxChip<ChipType>[]
        | null
        | undefined,
    ): OnyxChipsGroup<GroupType, ChipType>[] => {
      if (chips == null || !isArray(chips) || chips.length === 0) return [];
      if ('chips' in chips[0] && chips[0].chips) {
        return chips as OnyxChipsGroup<GroupType, ChipType>[];
      }

      return [
        {
          name: 'default',
          value: 'default' as GroupType,
          chips: chips as OnyxChip<ChipType>[],
        },
      ];
    },
  });
  public readonly variant = input<'primary' | 'secondary' | 'tertiary'>(
    'primary',
  );
  public readonly label = input<string>();
  public readonly single = input(false);
  public readonly selectAll = input(false);
  public readonly wrap = input<number | true>(9);
  public readonly gap = input(4);
  public readonly showGroups = input<boolean>();
  public readonly activeGroup = model<GroupType | null>(null);
  public readonly activeValues = model<ChipType[]>([]);

  protected readonly I18N = `${I18N_NAMESPACE}.chips`;

  protected displayGroups = computed(() => {
    const [groups, wrap] = [this.chips(), this.wrap()];
    const visibleChips = isNumber(wrap)
      ? Math.max(wrap - groups.length, 0)
      : Infinity;

    return groups.map((group) => ({
      ...group,
      chips: group.chips.slice(0, visibleChips),
      dropdown: group.chips.slice(
        visibleChips,
      ) satisfies OnyxOption<ChipType>[],
    }));
  });

  protected isSelectedAll = computed(() =>
    isEqual(this.activeValues()?.sort(), this.groupValues()?.sort()),
  );

  private group = computed(
    () =>
      this.chips().find(
        (group) => group.value === (this.activeGroup() ?? 'default'),
      ) ?? this.chips()[0],
  );
  private groupValues = computed(() =>
    this.group()?.chips.map((chip) => chip.value),
  );

  public ngOnInit(): void {
    let lastGroup: GroupType | null = null;
    effect(
      () => {
        if (this.chips().length === 0) return;
        if (lastGroup != null && this.activeGroup() === lastGroup) return;

        if (this.activeGroup() == null) {
          this.changeGroup(this.chips()[0].value);
        } else if (this.selectAll()) {
          this.selectAllChips();
        }

        lastGroup = this.activeGroup();
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const activeValues = this.activeValues();
        const groupValues = this.groupValues();
        if (activeValues?.length || !groupValues?.length) return;

        this.activeValues.set(
          this.selectAll() ? groupValues : groupValues.slice(0, 1),
        );
      },
      { injector: this.injector },
    );
  }

  protected selectAllChips(): void {
    this.activeValues.set(this.groupValues());
  }

  protected changeGroup(group: GroupType): void {
    const activeGroup = this.activeGroup() ?? 'default';
    if (activeGroup === group && this.selectAll()) {
      this.selectAllChips();
      return;
    }

    this.activeGroup.set(group);
    if (this.activeValues()?.length) return;

    this.activeValues.set(
      this.selectAll() ? this.groupValues() : this.groupValues().slice(0, 1),
    );
  }

  protected toggleChip(chip: OnyxChip<ChipType>, active: boolean): void {
    if (this.single() || (this.isSelectedAll() && this.selectAll())) {
      this.activeValues.set([chip.value]);
    } else if (!active) {
      this.activeValues.update((activeChipsValues) => {
        let values =
          activeChipsValues?.filter((value) => value !== chip.value) ?? null;
        if (!this.single() && this.selectAll() && values?.length === 0) {
          values = this.groupValues();
        }
        return values;
      });
    } else {
      this.activeValues.update((activeChipsValues) =>
        orderBy(
          [...new Set([...(activeChipsValues ?? []), chip.value])],
          (chip) => this.group().chips.findIndex((c) => c.value === chip),
        ),
      );
    }
  }
}
